/**
 * Pantalla pública de aceptación de invitación al pliego.
 *
 * Ruta: /invitacion/:token
 *
 * Flujo:
 *  1. Extrae el token de la URL (pathname).
 *  2. Consulta la tabla `pliego_invitaciones` por token (RLS pública mientras activo=true).
 *  3. Muestra el proyecto + rol + ramas que verá el usuario.
 *  4. Formulario simple: nombre + email + contraseña.
 *  5. Al enviar:
 *      · supabase.auth.signUp
 *      · UPSERT user_profiles con pliego_rol + pliego_project_id + pliego_visible_ramas
 *      · añade user_id a projects.shared_with
 *      · redirige a "/" (login o pliego según sesión).
 */
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const C = {
  bg: '#F5F0EA', dark: '#3B2F2F', text: '#2C2017', muted: '#7A6A5A',
  border: '#DDD5C8', accent: '#0E2C4B', olive: '#8D9A2E', coral: '#D4614A',
}
const FONT = "'DM Sans', -apple-system, sans-serif"

function getTokenFromPath() {
  if (typeof window === 'undefined') return null
  const m = window.location.pathname.match(/^\/invitacion\/([\w-]+)/i)
  return m ? m[1] : null
}

function ramaLabel(r) {
  return r === 'Instalaciones' ? 'Ingeniería' : r
}

const ROL_LABEL = {
  arquitectura: 'Arquitectura',
  ingenieria:   'Ingeniería',
  contratista:  'Contratista',
  promotor:     'Promotor',
}

export default function InvitacionPliego() {
  const token = getTokenFromPath()
  const [invitacion, setInvitacion] = useState(null)
  const [proyecto, setProyecto] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [creado, setCreado] = useState(false)
  const [errorForm, setErrorForm] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        setError('URL de invitación no válida.')
        setLoading(false)
        return
      }
      // Nota de seguridad: usamos la RPC get_pliego_invitacion_by_token (SECURITY
      // DEFINER) en vez de un SELECT abierto sobre la tabla, para no exponer la
      // lista completa de invitaciones (emails, nombres, tokens) al rol `anon`.
      // La RPC filtra internamente por token y devuelve 0 o 1 fila.
      const { data: rows, error: errInv } = await supabase
        .rpc('get_pliego_invitacion_by_token', { p_token: token })
      const inv = (rows && rows.length > 0) ? rows[0] : null
      if (cancelled) return
      if (errInv || !inv) {
        setError('Este enlace de invitación no es válido o ha expirado.')
        setLoading(false)
        return
      }
      // Cargar nombre del proyecto (SELECT público al project debería estar permitido para authenticated,
      // pero la invitación puede leerse sin login. Si el select falla, seguimos mostrando el resto.)
      const { data: proy } = await supabase
        .from('projects')
        .select('name, municipality, province')
        .eq('id', inv.project_id)
        .maybeSingle()
      if (cancelled) return
      setInvitacion(inv)
      setProyecto(proy)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [token])

  // Reglas de contraseña · deben cumplirse todas antes de habilitar el submit.
  // Coinciden con estándares comunes que la gente reconoce (8 chars, mayúscula,
  // minúscula, número). Supabase Auth exige mínimo 6 y valida contra HIBP;
  // pedir 8+mixto reduce la probabilidad de rechazo por HIBP.
  const reglasPassword = [
    { id: 'len',   ok: password.length >= 8,        texto: 'Al menos 8 caracteres' },
    { id: 'upper', ok: /[A-Z]/.test(password),      texto: 'Una letra mayúscula (A-Z)' },
    { id: 'lower', ok: /[a-z]/.test(password),      texto: 'Una letra minúscula (a-z)' },
    { id: 'num',   ok: /\d/.test(password),         texto: 'Un número (0-9)' },
  ]
  const passwordValida = reglasPassword.every(r => r.ok)

  function traducirErrorSignUp(err) {
    const msg = (err?.message || String(err || '')).toLowerCase()
    if (msg.includes('leak') || msg.includes('breach') || msg.includes('pwned') || msg.includes('weak_password')) {
      return 'Esta contraseña ha aparecido en filtraciones de datos públicas. Elige una menos común (evita palabras del diccionario, secuencias como 12345, o "password").'
    }
    if (msg.includes('already registered') || msg.includes('user already') || msg.includes('duplicate')) {
      return 'Ya existe una cuenta con este email. Inicia sesión en appbyhaus.com con tu contraseña habitual.'
    }
    if (msg.includes('rate limit')) {
      return 'Demasiados intentos seguidos. Espera un par de minutos y prueba otra vez.'
    }
    if (msg.includes('invalid email')) {
      return 'El email no tiene un formato válido.'
    }
    if (msg.includes('password') && msg.includes('short')) {
      return 'La contraseña es demasiado corta.'
    }
    return err?.message || String(err) || 'Error desconocido.'
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!invitacion) return
    if (!passwordValida) {
      setErrorForm('La contraseña no cumple todos los requisitos indicados.')
      return
    }
    setErrorForm(null)
    setEnviando(true)
    try {
      // 1. Registro en Supabase Auth (crea la fila en auth.users).
      //    password_set:true evita que App.js redirija a "Establece tu contraseña"
      //    al primer login (el usuario YA acaba de establecerla en este formulario).
      const { data: signData, error: signErr } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            nombre: nombre.trim(),
            pliego_rol: invitacion.pliego_rol,
            password_set: true,
          },
        },
      })
      if (signErr) {
        // Si el email ya existía en auth.users, el usuario probablemente había
        // sido invitado antes y ya tiene cuenta. Marcamos la invitación como
        // aceptada de todas formas (para que el panel HAUS muestre "Activa"),
        // pero no le dejamos avanzar sin autenticarse.
        const msg = (signErr.message || '').toLowerCase()
        if (msg.includes('already registered') || msg.includes('user already') || msg.includes('duplicate')) {
          await supabase.rpc('pliego_marcar_invitacion_aceptada', { p_token: invitacion.token })
        }
        throw signErr
      }
      const userId = signData?.user?.id
      if (!userId) throw new Error('No se ha podido crear la cuenta.')

      // 2. Toda la lógica server-side vía RPC (SECURITY DEFINER):
      //    - guarda user_profiles con full_name + pliego_rol + project_id + ramas
      //    - añade user_id a projects.shared_with
      //    - auto-confirma el email para login inmediato
      const { error: rpcErr } = await supabase.rpc('pliego_aceptar_invitacion', {
        p_token: invitacion.token,
        p_user_id: userId,
        p_full_name: nombre.trim(),
      })
      if (rpcErr) throw rpcErr

      setCreado(true)
    } catch (err) {
      setErrorForm(traducirErrorSignUp(err))
    } finally {
      setEnviando(false)
    }
  }

  const wrap = {
    minHeight: '100vh', background: C.bg, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: FONT,
  }
  const card = {
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px',
    padding: '2.5rem', maxWidth: '520px', width: '100%',
    boxShadow: '0 2px 12px rgba(59,47,47,0.08)',
  }
  const label = { display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }
  const input = { width: '100%', padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`,
                  borderRadius: '4px', fontSize: '0.9rem', fontFamily: FONT, boxSizing: 'border-box' }
  const btn = { width: '100%', padding: '0.75rem', background: C.accent, color: '#fff',
                border: 'none', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 600,
                cursor: 'pointer', fontFamily: FONT, marginTop: '0.5rem',
                opacity: enviando ? 0.6 : 1 }

  if (loading) {
    return <div style={wrap}><div style={card}>Cargando invitación…</div></div>
  }
  if (error) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={{ margin: 0, fontSize: '1.2rem', color: C.coral }}>Invitación no válida</h1>
          <p style={{ color: C.muted, marginTop: '0.75rem' }}>{error}</p>
          <p style={{ color: C.muted, fontSize: '0.85rem', marginTop: '1.5rem' }}>
            Si el enlace es de tu equipo BREEAM, pide una nueva invitación al asesor de HAUS.
          </p>
        </div>
      </div>
    )
  }
  if (creado) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={{ margin: 0, fontSize: '1.3rem', color: C.olive }}>✓ Cuenta creada</h1>
          <p style={{ color: C.text, marginTop: '0.75rem', lineHeight: 1.5 }}>
            Ya puedes iniciar sesión en{' '}
            <a href="/" style={{ color: C.accent }}>appbyhaus.com</a> con tu email{' '}
            <strong>{email}</strong> y la contraseña que acabas de crear.
          </p>
          <p style={{ color: C.muted, fontSize: '0.85rem', marginTop: '1rem' }}>
            Verás únicamente los Requisitos de Pliego correspondientes a{' '}
            <strong>{ROL_LABEL[invitacion.pliego_rol]}</strong>.
          </p>
          <button
            type="button"
            onClick={() => { window.location.href = '/' }}
            style={{ ...btn, display: 'block', textAlign: 'center' }}
          >
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.7rem', color: C.olive, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            HAUS · Invitación al Pliego BREEAM
          </div>
          <h1 style={{ margin: '0.35rem 0 0', fontSize: '1.4rem', color: C.text, letterSpacing: '-0.01em' }}>
            {proyecto?.name || 'Proyecto'}
          </h1>
          {proyecto?.municipality && (
            <div style={{ color: C.muted, fontSize: '0.8rem', marginTop: '0.15rem' }}>
              {proyecto.municipality}{proyecto.province ? ` · ${proyecto.province}` : ''}
            </div>
          )}
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ background: C.accent, color: '#fff', padding: '0.2rem 0.55rem',
                           borderRadius: '3px', fontSize: '0.7rem', fontWeight: 700 }}>
              Rol · {ROL_LABEL[invitacion.pliego_rol]}
            </span>
            <span style={{ background: C.bg, color: C.text, padding: '0.2rem 0.55rem',
                           borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600,
                           border: `1px solid ${C.border}` }}>
              Ramas visibles: {(invitacion.pliego_visible_ramas || []).map(ramaLabel).join(' · ')}
            </span>
          </div>
        </div>

        <p style={{ color: C.muted, fontSize: '0.85rem', lineHeight: 1.5, marginTop: 0, marginBottom: '1.5rem' }}>
          Crea tu cuenta con email y contraseña. Solo verás los Requisitos de Pliego
          correspondientes a tu equipo · podrás descargar guías, plantillas y listas de
          comprobación, y subir toda la documentación del criterio (memorias, planos,
          fichas técnicas, certificados, plantillas rellenas y cualquier otra evidencia).
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={label}>Nombre completo</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
                   required autoFocus style={input} placeholder="Ana García" />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={label}>Correo electrónico</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                   required style={input} placeholder="ana@ejemplo.com" />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={label}>Contraseña</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                   required style={input} autoComplete="new-password" />
            <ul style={{
              listStyle: 'none', padding: 0, margin: '0.6rem 0 0.35rem',
              fontSize: '0.78rem', lineHeight: 1.6,
            }}>
              {reglasPassword.map(r => (
                <li key={r.id} style={{
                  color: r.ok ? C.olive : C.muted,
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 14, height: 14, borderRadius: '50%',
                    background: r.ok ? C.olive : 'transparent',
                    border: r.ok ? 'none' : `1.5px solid ${C.border}`,
                    color: '#fff', fontSize: '10px', fontWeight: 700, lineHeight: 1,
                  }}>{r.ok ? '✓' : ''}</span>
                  {r.texto}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.25rem' }}>
              Evita contraseñas comunes como <code>12345678</code> o <code>password</code> — pueden ser rechazadas por seguridad.
            </div>
          </div>

          {errorForm && (
            <div style={{
              marginBottom: '1rem', padding: '0.6rem 0.75rem',
              background: '#FFF1ED', color: C.coral, border: `1px solid ${C.coral}`,
              borderRadius: 4, fontSize: '0.82rem', lineHeight: 1.45,
            }}>
              {errorForm}
            </div>
          )}

          <button type="submit" disabled={enviando || !passwordValida} style={{
            ...btn,
            opacity: (enviando || !passwordValida) ? 0.55 : 1,
            cursor: (enviando || !passwordValida) ? 'not-allowed' : 'pointer',
          }}>
            {enviando ? 'Creando cuenta…' : 'Crear cuenta y acceder al Pliego'}
          </button>
        </form>

        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}`,
                      fontSize: '0.75rem', color: C.muted, lineHeight: 1.5 }}>
          Si ya tienes cuenta, inicia sesión en{' '}
          <a href="/" style={{ color: C.accent }}>appbyhaus.com</a> con tu email y contraseña.
        </div>
      </div>
    </div>
  )
}
